import { TranslatePipe } from '../../filters/translate.pipe';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { InputDatepickerNewComponent } from '../../components/input-datepicker-new/input-datepicker-new.component';
import { ItemErrorComponent } from '../../components/item-form-error/item-form-error.component';
import { ItemNotificationComponent } from '../../components/item-notification/item-notification.component';
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import Session from '../../utils/session.service';
import RestService from '../../services/core-rest.service';
import ClaimService from '../../services/claim.service';
import InsuranceClaimFileService from '../../services/insurance-claim-file.service';
import AngularFilterHelperService from '../../services/angular-filter-helper.service';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import RoutingService from '../../services/routing.service';
import moment from 'moment';
import { FileChangeDirective } from '../../directives/file-change.directive';
import { MatSelectModule } from '@angular/material/select';

@Component({
	standalone: true,
	selector: 'insurance-claim-request',
	templateUrl: './insurance-claim-request.component.html',
	imports: [
		CommonModule, MatSelectModule, FileChangeDirective, TranslatePipe, 
		LoaderOverlayComponent, InputDatepickerNewComponent, 
		ItemErrorComponent, ItemNotificationComponent, FormsModule
	]
})
export default class InsuranceClaimRequestView implements OnInit {
	private session: Session = inject(Session);
	private restService: RestService = inject(RestService);
	private claimService: ClaimService = inject(ClaimService);
	private insuranceClaimFileService: InsuranceClaimFileService = inject(InsuranceClaimFileService);
	private angularFilterHelperService: AngularFilterHelperService = inject(AngularFilterHelperService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private routingService: RoutingService = inject(RoutingService);
	
	vm: any = undefined;
	
	constructor() {
		this.vm = this;
	}
	
	ngOnInit() {
		this.routingService.validateAccess('claimRequest').then(() => {
			this.onResolve();
		});
	}
	
	onResolve() {
		this.vm.previousStep = this.previousStep;
		this.vm.submitCurrentStep = this.submitCurrentStep;
		this.vm.triggerFileSelectionDialog = this.triggerFileSelectionDialog;
		this.vm.isConsentChecked = this.isConsentChecked;
		this.vm.uploadFile = this.uploadFile;
		this.vm.deleteFile = this.deleteFile;
		this.vm.goToClaimList = this.goToClaimList;
		this.vm.getContactUsInfo = this.getContactUsInfo;
		this.vm.checkClaimsSupported = this.checkClaimsSupported;
		
		this.vm.minDate = new Date(1985, 0, 1);
		this.vm.step = 1;
		this.vm.isInitialized = true;
		this.vm.isProcessing = false;
		var params: any = this.angularRoutingHelperService.getQueryParams();
		this.vm.isContinueProcessingMode = !!(params && params['claimId']);
		this.vm.user = this.session.getSession().user;
		this.vm.activePolicies = [];
		
		this.vm.caseDescription = {
			policyUser: undefined,
			treatmentPlace: '',
			treatmentDate: undefined,
			treatmentDescription: '',
			sumRequested: undefined
		};
		
		this.vm.fileUpload = {
			files: []
		};
		
		this.vm.paymentDetails = {
			accountHolderName: this.vm.user.firstname + ' ' + this.vm.user.lastname,
			accountNumber: '',
			bankName: '',
			sumRequested: ''
		};
		
		this.vm.contactDetails = {
			contactEmail: this.vm.user.email,
			contactPhone: this.vm.user.phone,
			consentsAccepted: {
				consent1: false,
				consent2: false
			}
		};
		
		this.vm.responses = {
			claimId: undefined
		};
		
		this.loadActivePolicyList();
		this.loadLastPaymentMethod();
		if (this.vm.isContinueProcessingMode) {
			this.continueExistingClaim();
		}
		
		this.vm.initialized = true;
	}
	
	loadLastPaymentMethod() {
		this.claimService.getPaymentMethodList(
			(response) => {
				if (response && response.length > 0) {
					this.vm.paymentDetails.accountHolderName = response[0].accountHolderName;
					this.vm.paymentDetails.accountNumber = response[0].accountNumber;
					this.vm.paymentDetails.bankName = response[0].bankName;
				}
			},
			(error) => {
				console.error(error);
			}
		);
	}
	
	loadActivePolicyList() {
		this.vm.isProcessing = true;
		this.restService.getUserActivePolicyList({})
			.then((activePolicies) => {
				this.vm.isProcessing = false;
				this.vm.activePolicies = activePolicies.map((item) => {
					return {
						id: item.id,
						userId: item.user.id,
						policyNumber: item.policy.policyNumber,
						displayName: item.policy.purchasedPackage.displayName,
						insuranceContact: item.policy.insuranceContact,
						claimsNotSupported: item.policy.claimsNotSupported
					}
				});
				if (this.vm.activePolicies.length > 0) {
					this.vm.caseDescription.policyUser = this.vm.activePolicies[0];
					if (this.vm.caseDescription.policyUser.claimsNotSupported) {
						this.vm.claimsNotSupported = true;
					}
				}
			})
			.catch((error) => {
				console.error(error);
			});
	}
	
	continueExistingClaim() {
		var claimId: any = (this.angularRoutingHelperService.getQueryParams())['claimId'];
		this.vm.isProcessing = true;
		this.claimService.getClaim(
			claimId,
			(response) => {
				this.vm.responses.claimId = claimId;
				this.vm.fileUpload.files = response['files']
					? response['files'].map((fileItem) => {
						return {
							id: fileItem.id,
							fileName: fileItem.fileName
						}
					}) : [];
				this.vm.caseDescription.treatmentPlace = response.treatmentPlace;
				this.vm.caseDescription.treatmentDate = response.treatmentDate;
				this.vm.caseDescription.treatmentDescription = response.treatmentDescription;
				this.vm.caseDescription.sumRequested = response.sumRequested;
				this.vm.paymentDetails.accountHolderName = response.paymentMethod
					? (response.paymentMethod.accountHolderName || (this.vm.user.firstname + ' ' + this.vm.user.lastname))
					: undefined;
				this.vm.paymentDetails.accountNumber = response.paymentMethod
					? response.paymentMethod.accountNumber
					: undefined;
				this.vm.paymentDetails.bankName = response.paymentMethod
					? response.paymentMethod.bankName
					: undefined;
				this.vm.paymentDetails.sumRequested = response.sumRequested;
				this.vm.isProcessing = false;
			}, (error) => {
				console.error(error);
			}
		);
	}
	
	triggerFileSelectionDialog() {
		var inputElement: any = document.getElementById('fileInput');
		inputElement ? inputElement.click() : undefined;
	}
	
	uploadFile = ($event, files): void => {
		this.vm.isProcessing = true;
		this.insuranceClaimFileService.uploadFileToClaim(
			this.vm.responses.claimId,
			files[0],
			(response) => {
				this.vm.isProcessing = false;
				this.vm.fileUpload.files.push({
					id: response.id,
					fileName: response.fileName
				})
			},
			(error) => {
				console.error(error);
			}
		);
	}
	
	deleteFile(file) {
		this.vm.isProcessing = true;
		this.insuranceClaimFileService.deleteFileFromClaim(
			file.id,
			(response) => {
				this.vm.isProcessing = false;
				this.vm.fileUpload.files = this.vm.fileUpload.files.filter((item) => {
					return item.id !== file.id;
				});
			},
			(error) => {
				console.error(error);
			}
		);
	}
	
	previousStep() {
		if (this.vm.step > 1) this.vm.step--;
	}
	
	checkClaimsSupported() {
		if (this.vm.caseDescription.policyUser.claimsNotSupported) {
			this.vm.claimsNotSupported = true;
		} else {
			this.vm.claimsNotSupported = false;
		}
	}
	
	submitCurrentStep(mainForm: any) {
		if (this.vm.activePolicies.length === 0) {
			this.goToClaimList();
			return;
		}
		if (this.vm.step === 1) {
			this.vm.caseDescriptionSubmitted = true;
			if (mainForm.invalid || this.vm.isProcessing || !this.vm.isInitialized) return;
			if (this.vm.caseDescription.policyUser.claimsNotSupported) {
				this.vm.claimsNotSupported = true;
				return;
			} else {
				this.vm.claimsNotSupported = false;
			}
			
			this.vm.isProcessing = true;
			var payload: any = {
				treatmentPlace: this.vm.caseDescription.treatmentPlace,
				treatmentDate: this.vm.caseDescription.treatmentDate ? moment(this.vm.caseDescription.treatmentDate).toDate() : undefined,
				treatmentDescription: this.vm.caseDescription.treatmentDescription,
				sumRequested: this.vm.caseDescription.sumRequested
			};
			if (this.vm.isContinueProcessingMode) {
				payload.id = this.vm.responses.claimId;
			} else {
				payload.policyUser = {
					id: this.vm.caseDescription.policyUser.id
				};
			}
			this.restService.saveUserClaimsList(payload)
				.then((response) => {
					this.vm.isProcessing = false;
					this.vm.isContinueProcessingMode = true;
					this.vm.responses.claimId = response.id;
					this.vm.step++;
				}).catch((error) => {
					console.error(error);
				});
		} else if (this.vm.step === 2) {
			if (this.vm.fileUpload.files.length === 0) return;
			
			this.vm.step++;
		} else if (this.vm.step === 3) {
			this.vm.paymentDetailsSubmitted = true;
			if (mainForm.invalid || this.vm.isProcessing || !this.vm.isInitialized) return;
			
			var paymentMethodData = {
				claim: { id: this.vm.responses.claimId },
				accountNumber: this.vm.paymentDetails.accountNumber,
				accountHolderName: this.vm.paymentDetails.accountHolderName,
				bankName: this.vm.paymentDetails.bankName
			};
			this.vm.isProcessing = true;
			this.restService.saveUpdateClaimPaymentMethod(paymentMethodData)
				.then((response) => {
					this.vm.isProcessing = false;
					this.vm.step++;
					// this.restService.saveUserClaimsList({
					//     id: this.vm.responses.claimId,
					//     sumRequested: this.vm.paymentDetails.sumRequested
					// }).then((response) => {
					//     this.vm.isProcessing = false;
					//     this.vm.step++;
					// }).catch((error) => {
					//     console.error(error);
					// });
				}).catch((error) => {
					console.error(error);
				});
		} else if (this.vm.step === 4) {
			this.vm.contactDetailsSubmitted = true;
			if (mainForm.invalid || this.vm.isProcessing || !this.vm.isInitialized) return;
			
			this.vm.isProcessing = true;
			var payload: any = {
				id: this.vm.responses.claimId,
				contactEmail: this.vm.contactDetails.contactEmail,
				contactPhone: this.vm.contactDetails.contactPhone,
				consentsAccepted: true
			};
			this.restService.saveSubmitClaim(payload)
				.then((response) => {
					this.vm.isProcessing = false;
					this.vm.step++;
				}).catch((error) => {
					console.error(error);
				});
		}
	}
	
	isConsentChecked() {
		return this.vm.contactDetails.consentsAccepted.consent1
			&& this.vm.contactDetails.consentsAccepted.consent2;
	}
	
	goToClaimList() {
		this.angularRoutingHelperService.setPath('/insurance/claims');
	}
	
	getContactUsInfo() {
		if (this.vm.caseDescription.policyUser
		&& this.vm.caseDescription.policyUser.insuranceContact) {
			var contactUs: any = '';
			if (this.vm.caseDescription.policyUser.insuranceContact.email
				&& this.vm.caseDescription.policyUser.insuranceContact.phone) {
				contactUs = this.angularFilterHelperService.translate('Claim_Request.Contact_Us_Phone_Or_Email');
			} else if (this.vm.caseDescription.policyUser.insuranceContact.email) {
				contactUs = this.angularFilterHelperService.translate('Claim_Request.Contact_Us_Email_Only');
			} else if (this.vm.caseDescription.policyUser.insuranceContact.phone) {
				contactUs = this.angularFilterHelperService.translate('Claim_Request.Contact_Us_Phone_Only');
			}
			if (this.vm.caseDescription.policyUser.insuranceContact.startTime) {
				contactUs = contactUs.replace('%s$1', this.vm.caseDescription.policyUser.insuranceContact.startTime);
			} else {
				contactUs = contactUs.replace('%s$1', '00:00');
			}
			if (this.vm.caseDescription.policyUser.insuranceContact.endTime) {
				contactUs = contactUs.replace('%s$2', this.vm.caseDescription.policyUser.insuranceContact.endTime);
			} else {
				contactUs = contactUs.replace('%s$2', '24:00');
			}
			if (this.vm.caseDescription.policyUser.insuranceContact.phone) {
				contactUs = contactUs.replace('%s$3', this.vm.caseDescription.policyUser.insuranceContact.phone);
			}
			if (this.vm.caseDescription.policyUser.insuranceContact.email) {
				contactUs = contactUs.replace('%s$4', this.vm.caseDescription.policyUser.insuranceContact.email);
			}
			return contactUs;
		} else {
			return '';
		}
	}
	
}